import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * shadcn/ui standard `cn` helper: merge conditional class names
 * and resolve Tailwind class conflicts.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/**
 * Random id for local React list keys.
 *
 * `crypto.randomUUID()` only exists in a secure context (HTTPS or localhost).
 * The TEST VM is served over plain HTTP, where reading it yields `undefined`
 * and calling it throws — which killed the click handler behind the curation
 * filter "+ 조건 추가" button. `crypto.getRandomValues()` has no such gate, so
 * build the v4 UUID from it when `randomUUID` is missing.
 */
export function randomId(): string {
  const c = globalThis.crypto
  if (typeof c?.randomUUID === 'function') return c.randomUUID()

  if (typeof c?.getRandomValues === 'function') {
    const bytes = c.getRandomValues(new Uint8Array(16))
    bytes[6] = (bytes[6] & 0x0f) | 0x40 // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80 // variant 10
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }

  // 마지막 방어선: 두 API 가 모두 없어도 UI 가 멈추지 않게 한다.
  // 이 값은 브라우저 안의 React key 로만 쓰이고 서버로 전송되지 않는다.
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * 클립보드 복사 — 보안 컨텍스트가 아닌 곳에서도 동작한다.
 *
 * `navigator.clipboard` 는 HTTPS 나 localhost 에서만 정의된다. TEST VM 은 평문
 * HTTP 로 서비스되므로 그곳에서는 `undefined` 이고, `.writeText()` 를 그대로
 * 부르면 핸들러가 통째로 죽는다 (`randomId` 와 같은 원인이다). 그래서 구형
 * `execCommand('copy')` 를 폴백으로 둔다.
 *
 * @returns 복사에 성공했으면 `true`. `false` 면 호출측이 직접 선택해 복사하라고
 *   안내해야 한다 — 복사된 척하는 토스트가 제일 나쁘다.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // 권한 거부·포커스 없음 등. 아래 폴백으로 넘어간다.
  }

  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.top = '-9999px'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

/**
 * 바이트 크기를 사람이 읽는 단위로. 1024 기준.
 *
 * QA O-4 로 목록 카드에도 용량을 띄우면서 세 번째 사본이 생길 뻔했다.
 * MonitoringPage · CurationDetailPage 의 지역 구현을 여기로 합친다.
 *
 * `null` / `undefined` / 0 이하는 `—` 다. 용량을 모르는 것과 0 바이트는
 * 화면에서 구분할 의미가 없고, `0 B` 라고 단정하면 없는 사실을 말하게 된다.
 */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || bytes <= 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)))
  const value = bytes / 1024 ** i
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}
